import { Auth } from 'aws-amplify';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import { Grid, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useDialog } from 'muibox';
import PasswordStrengthBar from 'react-password-strength-bar';
import { COGNITO_PASSWORD_MIN_LENGTH, describeChangePasswordError, getNewPasswordProblem } from './changePasswordHelpers';

const ProfileDialogChangePassword = () => {
    const dialog = useDialog();

    const [oldPassword, setOldPassword] = useState<string>('');
    const [newPassword, setNewPassword] = useState<string>('');
    const [repeatNewPassword, setRepeatNewPassword] = useState<string>('');
    const [isChanging, setIsChanging] = useState<boolean>(false);

    const newPasswordProblem = getNewPasswordProblem(newPassword, repeatNewPassword);

    const canSubmit = !isChanging && oldPassword.length > 0 && newPassword.length > 0 && repeatNewPassword.length > 0 && !newPasswordProblem;

    const handleChangePassword = () => {
        if (!canSubmit) {
            return;
        }

        // Disable the button for the whole request: a second click after the first request
        // succeeded would be sent with the now-wrong old password and fail.
        setIsChanging(true);

        Auth.currentAuthenticatedUser()
            .then(user => Auth.changePassword(user, oldPassword, newPassword))
            .then(() => {
                setOldPassword('');
                setNewPassword('');
                setRepeatNewPassword('');

                dialog
                    .alert({
                        title: 'Success',
                        message: 'Password changed successfully',
                        ok: {
                            text: 'OK',
                            color: 'primary',
                            variant: 'contained',
                        },
                    })
                    .then(() => {})
                    .catch(() => {});
            })
            .catch(err => {
                console.error('Change password failed', err);

                dialog
                    .alert({
                        title: 'Error',
                        message: describeChangePasswordError(err),
                        ok: {
                            text: 'OK',
                            color: 'error',
                            variant: 'contained',
                        },
                    })
                    .then(() => {})
                    .catch(() => {});
            })
            .finally(() => {
                setIsChanging(false);
            });
    };

    return (
        <Grid container spacing={1} alignItems="center">
            <Grid item xs={12}>
                <Typography variant="h6">Change Password</Typography>
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label="Old Password"
                    name="oldPassword"
                    type="password"
                    autoComplete="current-password"
                    fullWidth
                    value={oldPassword}
                    disabled={isChanging}
                    onChange={e => setOldPassword(e.target.value)}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label="New Password"
                    name="newPassword"
                    type="password"
                    autoComplete="new-password"
                    fullWidth
                    value={newPassword}
                    disabled={isChanging}
                    onChange={e => setNewPassword(e.target.value)}
                    helperText={`At least ${COGNITO_PASSWORD_MIN_LENGTH} characters`}
                />
                <PasswordStrengthBar password={newPassword} minLength={COGNITO_PASSWORD_MIN_LENGTH} />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    label="Repeat New Password"
                    name="repeatNewPassword"
                    type="password"
                    autoComplete="new-password"
                    fullWidth
                    value={repeatNewPassword}
                    disabled={isChanging}
                    onChange={e => setRepeatNewPassword(e.target.value)}
                    error={!!newPasswordProblem}
                    helperText={newPasswordProblem ?? ''}
                />
            </Grid>
            <Grid item xs={12}>
                <LoadingButton
                    variant="contained"
                    color="secondary"
                    fullWidth
                    size="large"
                    loading={isChanging}
                    disabled={!canSubmit}
                    onClick={handleChangePassword}
                >
                    Change
                </LoadingButton>
            </Grid>
        </Grid>
    );
};

export default ProfileDialogChangePassword;
